/**
 * Query builder helpers for common operations
 */
import type { Knex } from 'knex'
import type { EntityDefinition, ServerEntityQuery, PaginatedResult } from '@gzl10/nexus-sdk'

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20
export const DEFAULT_MAX_LIMIT = 100

export interface PaginationParams {
  page: number
  limit: number
  offset: number
}

/**
 * Extract pagination parameters from query.
 * Validates and normalizes page/limit values.
 */
export function getPagination(query?: ServerEntityQuery): PaginationParams {
  const maxLimit = query?.maxLimit ?? DEFAULT_MAX_LIMIT
  const page = Math.max(1, query?.page ?? DEFAULT_PAGE)
  const limit = Math.min(maxLimit, Math.max(1, query?.limit ?? DEFAULT_LIMIT))
  const offset = (page - 1) * limit
  return { page, limit, offset }
}

/**
 * Build a PaginatedResult from items and total count.
 */
export function buildPaginatedResult<T>(
  items: T[],
  total: number,
  pagination: PaginationParams
): PaginatedResult<T> {
  const { page, limit } = pagination
  const totalPages = Math.ceil(total / limit)
  return {
    items,
    total,
    page,
    limit,
    totalPages,
    hasNext: page < totalPages
  }
}

/**
 * Get searchable field names from entity definition
 */
export function getSearchableFields(definition: EntityDefinition): string[] {
  return Object.entries(definition.fields ?? {})
    .filter(([_, f]) => f.meta?.searchable === true)
    .map(([name]) => name)
}

/**
 * Apply search filter to query builder based on entity definition.
 * Searches in fields with meta.searchable: true, or falls back to labelField.
 */
export function applySearchFilter(
  qb: Knex.QueryBuilder,
  definition: EntityDefinition,
  search: string
): Knex.QueryBuilder {
  const searchableFields = getSearchableFields(definition)

  // Fallback to labelField if no searchable fields
  if (searchableFields.length === 0 && 'labelField' in definition && definition.labelField) {
    searchableFields.push(definition.labelField as string)
  }

  // Apply OR search across all searchable fields
  if (searchableFields.length > 0) {
    const searchPattern = `%${search}%`
    qb.where(function () {
      for (const field of searchableFields) {
        this.orWhere(field, 'like', searchPattern)
      }
    })
  }

  return qb
}
